import React, { useState, useEffect } from 'react';
import './TodaysTasks.css';
import TaskTimer from '../../Tasks/components/TaskTimer/TaskTimer';

function TodaysTasks({ onTaskComplete, onViewAll }) {
  const [tasks, setTasks] = useState([]);
  const [tasksByStage, setTasksByStage] = useState({
    lead: [],
    customer: [],
    marketer: [],
    leader: [],
    other: []
  });
  const [stageCounts, setStageCounts] = useState({
    lead: 0,
    customer: 0,
    marketer: 0,
    leader: 0,
    other: 0
  });
  const [loading, setLoading] = useState(true);
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  useEffect(() => {
    fetchTodaysTasks();
  }, []);

  const fetchTodaysTasks = async () => {
    try {
      const response = await fetch('/backend/api/tasks/todays-tasks.php', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setTasks(data.tasks);
        setTasksByStage(data.tasksByStage || {
          lead: [],
          customer: [],
          marketer: [],
          leader: [],
          other: []
        });
        setStageCounts(data.stageCounts || {
          lead: 0,
          customer: 0,
          marketer: 0,
          leader: 0,
          other: 0
        });
      }
    } catch (error) {
      console.error('Error fetching today\'s tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskAction = async (taskId, action) => {
    try {
      const response = await fetch('/backend/api/tasks/complete-task.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          task_id: taskId,
          action: action
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Remove task from list
        setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
        
        // Update tasksByStage
        setTasksByStage(prevTasksByStage => {
          const updated = { ...prevTasksByStage };
          Object.keys(updated).forEach(stage => {
            updated[stage] = updated[stage].filter(task => task.id !== taskId);
          });
          return updated;
        });
        
        // Update stage counts
        setStageCounts(prevCounts => {
          const updated = { ...prevCounts };
          // Find which stage the task was in and decrement count
          Object.keys(tasksByStage).forEach(stage => {
            if (tasksByStage[stage].some(task => task.id === taskId)) {
              updated[stage] = Math.max(0, updated[stage] - 1);
            }
          });
          return updated;
        });
        
        // Notify parent component if callback provided
        if (onTaskComplete) {
          onTaskComplete();
        }
      }
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Failed to update task. Please try again.');
    }
  };

  const handleContactClick = (contactId) => {
    // This would open the Tasks popup for the contact
    // For now, we'll just log it
    console.log('Open tasks for contact:', contactId);
  };

  const toggleExpand = (taskId) => {
    setExpandedTaskId(expandedTaskId === taskId ? null : taskId);
  };

  const truncate = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="todays-tasks-card">
        <h3 className="todays-tasks-title">Today's Tasks</h3>
        <div className="loading-spinner">Loading tasks...</div>
      </div>
    );
  }

  const stages = [
    { key: 'lead', name: 'Leads', color: '#3b82f6', icon: '👥' },
    { key: 'customer', name: 'Customers', color: '#10b981', icon: '🛒' },
    { key: 'marketer', name: 'Marketers', color: '#f59e0b', icon: '🚀' },
    { key: 'leader', name: 'Leaders', color: '#8b5cf6', icon: '👑' },
    { key: 'other', name: 'Other', color: '#6b7280', icon: '📋' }
  ];

  const totalTasks = tasks.length;

  return (
    <div className="todays-tasks-card">
      <div className="tasks-title-row">
        <h3 className="todays-tasks-title">
          Today's Tasks
          {totalTasks > 0 && <span className="task-count-badge">{totalTasks}</span>}
        </h3>
        {onViewAll && (
          <button className="btn-view-all" onClick={onViewAll}>
            View All
          </button>
        )}
      </div>
      
      {totalTasks > 0 ? (
        <div className="tasks-list-scrollable">
          {stages.map((stage) => {
            const stageTasks = tasksByStage[stage.key] || [];
            if (stageTasks.length === 0) return null;
            
            return (
              <div key={stage.key}>
                <div className="stage-mini-header" style={{ '--stage-color': stage.color }}>
                  {stage.icon} {stage.name} ({stageTasks.length})
                </div>
                {stageTasks.map((task) => (
                  <div 
                    key={task.id} 
                    className={`task-widget-row ${task.is_overdue ? 'overdue' : ''}`}
                    onClick={() => toggleExpand(task.id)}
                  >
                    <div className="task-row-left">
                      <span className="task-contact-name">
                        {task.contact_name ? (
                          <span 
                            className="clickable" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContactClick(task.contact_id);
                            }}
                          >
                            {task.contact_name}
                          </span>
                        ) : (
                          'General Task'
                        )}
                      </span>
                      <span className="task-brief-desc">{truncate(task.description, 40)}</span>
                    </div>
                    
                    <div className="task-row-right">
                      {task.is_overdue && <span className="task-status-overdue">⏰ OVERDUE</span>}
                      {!task.is_overdue && task.deadline_timestamp && (
                        <TaskTimer 
                          deadlineTimestamp={task.deadline_timestamp} 
                          compact={true}
                          completedBeforeGeneration={task.completed_before_generation}
                        />
                      )}
                      <button className="expand-icon">
                        {expandedTaskId === task.id ? '▲' : '▼'}
                      </button>
                    </div>
                    
                    {expandedTaskId === task.id && (
                      <div className="task-details-expanded">
                        <p className="full-description">{task.description}</p>
                        <div className="task-actions-row">
                          <button 
                            className="task-complete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskAction(task.id, 'complete');
                            }}
                          >
                            ✓ Complete
                          </button>
                          <button 
                            className="task-dismiss-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskAction(task.id, 'dismiss');
                            }}
                          >
                            ✕ Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="no-tasks">
          <p>Great job! No tasks for today 🎉</p>
        </div>
      )}
    </div>
  );
}

export default TodaysTasks;
